export function formatNumberValueIdeas(num: number, prefix = "$"): string {
  // Format the number with up to 2 decimal places
  const formatted = num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    
  // Remove trailing zeros from the decimal part
  
  const withDollarSign = `${prefix}${formatted.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, '')}`
  
  return withDollarSign;
}

export function formatMonetaryValueIdeas(num: number, prefix = "$") {
  if (num < 10000) {
    return formatNumberValueIdeas(num, prefix);
  } else if (num < 1000000) {
    return `${prefix}${Math.round(num / 1000)?.toLocaleString()}k`;
  } else {
    return `${prefix}${(num / 1000000)?.toFixed(1)?.toLocaleString()}M`;
  }
}
  
export function formatBigMonetaryValueIdeas(num: number, prefix = "$") {
  if (num <= 99000000) {
    return formatMonetaryValueIdeas(num, prefix);
  } else if (num > 99000000){
    return `${prefix}${Math.round(num / 1000000)?.toLocaleString()}M`;
  }
}

export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  return path
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
};

/** Keys used for evidence/customer/amount rollups on idea rows (same triple as `aggregations.*.{state}`). */
const ROLLUP_STATES = ["assigned", "suggested", "total"] as const;

/**
 * Detects `{ assigned?, suggested?, total? }` where every own key is one of those
 * (e.g. `productItem.customerNames`, `productItem.opportunityAmount` from $lookup).
 * `DynamicCell` uses {@link updateAccessorByState} in `utils.ts` for paths containing `"aggregations"` only;
 * joined `productItem.*` fields often ship this shape as a single object instead.
 */
export function isAssignedSuggestedTotalRollup(val: unknown): val is Record<string, unknown> {
  if (val == null || typeof val !== "object" || Array.isArray(val)) return false;
  const o = val as Record<string, unknown>;
  const keys = Object.keys(o as object);
  if (keys.length === 0) return false;
  return keys.every((k) => (ROLLUP_STATES as readonly string[]).includes(k));
}

function normalizeRollupSortState(sortState: string): "assigned" | "suggested" | "total" {
  if (sortState === "assigned" || sortState === "suggested" || sortState === "total") return sortState;
  return "total";
}

/** True when every present branch is a number (monetary rollup), not string[] lists. */
function rollupObjectIsMonetary(o: Record<string, unknown>): boolean {
  for (const k of ROLLUP_STATES) {
    if (!(k in o)) continue;
    const v = o[k];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) return false;
    if (typeof v !== "number" || Number.isNaN(v)) return false;
  }
  return ROLLUP_STATES.some((k) => k in o && o[k] != null);
}

function formatRollupLeaf(
  leaf: unknown,
  monetary: boolean,
  prefix: string
): string | number {
  if (leaf == null) return "";
  if (typeof leaf === "number") {
    return monetary ? formatBigMonetaryValueIdeas(leaf, prefix) : leaf;
  }
  if (typeof leaf === "boolean") return String(leaf);
  if (typeof leaf === "string") return leaf;
  if (Array.isArray(leaf)) {
    return leaf
      .map((x) => {
        if (x == null) return "";
        if (typeof x === "string" || typeof x === "number") return String(x);
        if (typeof x === "boolean") return String(x);
        if (typeof x === "object") {
          const r = x as Record<string, unknown>;
          const pick =
            (typeof r.name === "string" && r.name) ||
            (typeof r.label === "string" && r.label) ||
            (typeof r.title === "string" && r.title);
          if (pick) return pick;
        }
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof leaf === "object") {
    const x = leaf as Record<string, unknown>;
    const pick =
      (typeof x.name === "string" && x.name) ||
      (typeof x.label === "string" && x.label) ||
      (typeof x.title === "string" && x.title);
    if (pick) return pick;
    try {
      const j = JSON.stringify(leaf);
      return j.length > 120 ? `${j.slice(0, 117)}…` : j;
    } catch {
      return "";
    }
  }
  return String(leaf);
}

/**
 * Formats assigned|suggested|total rollup objects for TextCell and similar.
 * @returns null if `val` is not this shape.
 */
export function formatIdeasRollupCellValue(
  val: unknown,
  sortState: string,
  renderParams?: { prefix?: string }
): string | number | null {
  if (!isAssignedSuggestedTotalRollup(val)) return null;
  const o = val as Record<string, unknown>;
  const state = normalizeRollupSortState(sortState);
  const prefix = renderParams?.prefix ?? "$";
  const monetary = rollupObjectIsMonetary(o);

  let pick: unknown = o[state];
  if (Array.isArray(pick) && pick.length === 0) pick = undefined;
  if (pick === undefined || pick === null) {
    pick =
      o.total !== undefined && o.total !== null
        ? o.total
        : o.assigned !== undefined && o.assigned !== null
          ? o.assigned
          : o.suggested;
  }
  if (pick === undefined || pick === null) return "";

  return formatRollupLeaf(pick, monetary, prefix);
}